import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Stack,
  Checkbox,
  FormControlLabel,
  Box,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useState } from "react";
import api from "../../api/axios";

export default function AddBoreholeProgressModal({
  open,
  onClose,
  projectName,
  borehole,
  projectId,
  onSuccess,
}) {
  const [date, setDate] = useState(null);
  const [meterage, setMeterage] = useState("");
  const [markClosure, setMarkClosure] = useState(false);
  const [isAbandoned, setIsAbandoned] = useState(false);
  const [errors, setErrors] = useState({});

  /* ========================================================= */
  /* VALIDATION */
  /* ========================================================= */

  const validate = (abandoned = false) => {
    const newErrors = {};

    // Date is always mandatory
    if (!date || !dayjs(date).isValid()) {
      newErrors.date = "Date is required";
    }

    // Meterage is mandatory only for normal progress entries
    const isClosureOrAbandonment = markClosure || isAbandoned || abandoned;

    if (!isClosureOrAbandonment) {
      if (
        meterage === "" ||
        !Number.isFinite(Number(meterage)) ||
        Number(meterage) <= 0
      ) {
        newErrors.meterage = "Enter valid meterage";
      }
    } else if (
      meterage !== "" &&
      (!Number.isFinite(Number(meterage)) || Number(meterage) < 0)
    ) {
      newErrors.meterage = "Enter valid meterage";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* ========================================================= */
  /* SUBMIT */
  /* ========================================================= */

  const submitProgress = async (abandoned = false) => {
    if (!validate(abandoned)) return;

    try {
      await api.post(`/projects/exploration/save_bh_progress/${projectId}`, {
        boreholeNumber: borehole.id,
        date: dayjs(date).format("YYYY-MM-DD"),
        meterage: meterage === "" ? null : Number(meterage),
        markClosure,
        isAbandoned: abandoned || isAbandoned,
      });

      onSuccess();
      handleClose();
    } catch (err) {
      console.error(
        abandoned
          ? "Failed to abandon borehole"
          : "Failed to add borehole progress",
        err,
      );

      const message =
        err?.response?.data?.message ||
        (abandoned
          ? "Failed to abandon borehole"
          : "Failed to add borehole progress");

      alert(message);
    }
  };

  /* ========================================================= */
  /* NORMAL SUBMIT */
  /* ========================================================= */

  const handleSubmit = async () => {
    await submitProgress(false);
  };

  /* ========================================================= */
  /* ABANDON BOREHOLE */
  /* ========================================================= */

  const handleAbandon = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to abandon Borehole "${borehole?.borehole_code}"?\n\nThis action will mark the borehole as abandoned.`,
    );

    if (!confirmed) {
      return;
    }

    await submitProgress(true);
  };

  /* ========================================================= */
  /* CLOSE / RESET */
  /* ========================================================= */

  const handleClose = () => {
    setDate(null);
    setMeterage("");
    setMarkClosure(false);
    setIsAbandoned(false);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <DialogTitle>
        <Typography fontWeight={700}>{projectName}</Typography>

        <Typography variant="body2" color="text.secondary">
          Borehole: <strong>{borehole.borehole_code}</strong>
        </Typography>
      </DialogTitle>

      {/* ===================================================== */}
      {/* FORM */}
      {/* ===================================================== */}

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* PROGRESS DATE */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Progress Date"
              value={date}
              onChange={(newValue) => {
                setDate(newValue);

                if (newValue) {
                  setErrors((prev) => ({
                    ...prev,
                    date: "",
                  }));
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(errors.date),
                  helperText: errors.date,
                },
              }}
              disableFuture
            />
          </LocalizationProvider>

          {/* METERAGE */}
          <TextField
            label="Meterage (m)"
            type="number"
            value={meterage}
            error={Boolean(errors.meterage)}
            helperText={errors.meterage}
            onChange={(e) => {
              const value = e.target.value;

              setMeterage(value);

              if (value === "" || Number(value) >= 0) {
                setErrors((prev) => ({
                  ...prev,
                  meterage: "",
                }));
              }
            }}
            fullWidth
            inputProps={{
              min: 0,
              step: "any",
            }}
          />

          {/* MARK CLOSURE */}
          <FormControlLabel
            control={
              <Checkbox
                checked={markClosure}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setMarkClosure(checked);

                  // Closure and abandonment are mutually exclusive
                  if (checked) {
                    setIsAbandoned(false);
                  }
                }}
              />
            }
            label="Mark Borehole Closure"
          />

          {/* ABANDONMENT INFORMATION */}
          {isAbandoned && (
            <Typography
              variant="caption"
              sx={{
                color: "error.main",
                mt: -1,
              }}
            >
              This borehole will be marked as abandoned.
            </Typography>
          )}
        </Stack>
      </DialogContent>

      {/* ===================================================== */}
      {/* ACTIONS */}
      {/* ===================================================== */}

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        {/* LEFT SIDE — ABANDON */}
        <Button
          variant="contained"
          color="error"
          onClick={handleAbandon}
          disabled={markClosure}
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          Abandon Borehole
        </Button>

        {/* RIGHT SIDE — CANCEL / SUBMIT */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
          }}
        >
          <Button onClick={handleClose}>Cancel</Button>

          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
